import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShieldCheck } from "lucide-react";
import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CTABanner } from "../components/sections/CTABanner";
import { VendorMarquee } from "../components/sections/VendorStack";

const BASE = 394;
const rateFor = (users) => Math.round(BASE * (1 - 0.01 * (users - 1)) * 100) / 100;
const fmt = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GROUPS = [
  { label: "Managed IT", items: "24/7 monitoring & alerting · automated patching · vendor management · network & firewall management · hardware lifecycle planning · quarterly business reviews · IT budgeting · dedicated virtual CIO" },
  { label: "Cybersecurity — powered by Huntress", items: "Managed EDR with 24/7 human-led SOC · Managed ITDR for Microsoft 365 identities · Managed SIEM · security awareness training with phishing simulations · dark-web monitoring · vulnerability management · HIPAA compliance support · incident response planning" },
  { label: "Cloud & Microsoft 365", items: "Exchange, Teams, SharePoint & OneDrive administration · onboarding & offboarding · license management · conditional access · Entra ID management · migrations · governance" },
  { label: "Help Desk — unlimited, always", items: "24/7/365 support by phone or email · assigned engineer's direct cell once you transition · no caps on support hours, ever · no per-ticket or per-call charges · 15-minute critical response target · named account manager · on-site dispatch" },
  { label: "Backup & DR — powered by MSP360", items: "Automated daily cloud backup · Microsoft 365 cloud-to-cloud backup · immutable ransomware-proof copies · item-level & point-in-time recovery · quarterly restore testing · documented DR plan · annual DR drill" },
];

export default function PricingPage() {
  const [users, setUsers] = useState(10);
  const rate = rateFor(users);
  const monthly = fmt(rate * users);
  const saving = users - 1;

  return (
    <>
      <Seo
        title="Pricing | The Sempresera Standard — National Service Provider"
        description="One all-inclusive managed IT plan starting at $394.00 per user per month — every user on your plan lowers the rate by 1%. Unlimited support hours, no tiers, no surprise bills."
        path="/pricing"
      />

      <section className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pt-44" data-testid="pricing-page">
        <MaskedLine delay={0.1}>
          <span className="eyebrow">Pricing — one plan, everyone is a starter</span>
        </MaskedLine>
        <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-6xl lg:text-7xl">
          <MaskedLine delay={0.25}>
            <span className="text-metallic">The Sempresera</span>
          </MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-metallic">Standard.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.55}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#C9D2DE] md:text-lg">
            Whether you're a sole attorney or a 25-person firm, you get the same full team, the same full stack, the
            same 24/7 attention. What other MSPs charge extra for is simply our standard.
          </p>
        </MaskedLine>

        <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="glass h-full p-8 md:p-10" data-testid="pricing-card">
              <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#A8B4C2]">Everything included, for everyone</p>
              <div className="mt-8 space-y-7">
                {GROUPS.map((g) => (
                  <div key={g.label}>
                    <p className="font-display text-sm font-bold text-brand">{g.label}</p>
                    <p className="mt-2 text-sm leading-relaxed text-[#C9D2DE]">{g.items}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10 flex items-start gap-3 border-t border-white/10 pt-8">
                <ShieldCheck size={20} className="mt-0.5 shrink-0 text-brand" strokeWidth={1.5} />
                <p className="text-sm leading-relaxed text-[#C9D2DE]">
                  Built for confidentiality: attorneys, accountants and client-privilege professions trust Sempresera
                  with their most sensitive data — protected by least-privilege access and immutable records.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="glass flex h-full flex-col p-8 md:p-10" data-testid="pricing-calculator">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-6xl font-black tracking-tight text-metallic md:text-7xl" data-testid="pricing-rate">
                  ${fmt(rate)}
                </span>
                <span className="text-sm text-[#A8B4C2]">/ user / month</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-[#C9D2DE]">
                Starts at ${BASE.toFixed(2)} per user per month — and every user on your plan lowers the per-user
                rate by 1%. Growth is rewarded, never gated.
              </p>

              <div className="mt-10">
                <div className="flex items-center justify-between">
                  <label htmlFor="team-size" className="field-label !mb-0">Your team size</label>
                  <span className="font-display text-xl font-bold text-brand" data-testid="team-size-value">{users} {users === 1 ? "user" : "users"}</span>
                </div>
                <input
                  id="team-size"
                  type="range"
                  min={1}
                  max={25}
                  value={users}
                  onChange={(e) => setUsers(Number(e.target.value))}
                  className="team-slider mt-4 w-full"
                  data-testid="team-size-slider"
                />
                <div className="relative mt-3 h-4 text-xs text-zinc-600" data-testid="team-size-scale">
                  {[1, 5, 10, 25].map((m) => (
                    <span
                      key={m}
                      className="absolute -translate-x-1/2"
                      style={{ left: `calc(8px + (100% - 16px) * ${(m - 1) / 24})` }}
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-10 border-t border-white/10 pt-8">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-[#C9D2DE]">Estimated monthly total</span>
                  <span className="font-display text-3xl font-black text-metallic" data-testid="pricing-monthly-total">${monthly}</span>
                </div>
                {saving > 0 && (
                  <p className="mt-2 text-right font-display text-xs font-bold uppercase tracking-[0.2em] text-brand" data-testid="pricing-discount-label">
                    {saving}% volume saving applied
                  </p>
                )}
                <ul className="mt-6 space-y-2 text-xs text-[#A8B4C2]">
                  <li className="flex gap-2"><Check size={14} className="mt-0.5 shrink-0 text-brand" /> 1 user: ${BASE.toFixed(2)} per user per month</li>
                  <li className="flex gap-2"><Check size={14} className="mt-0.5 shrink-0 text-brand" /> Every additional user lowers the rate by a further 1%</li>
                  <li className="flex gap-2"><Check size={14} className="mt-0.5 shrink-0 text-brand" /> Unlimited support hours — never a per-ticket charge</li>
                </ul>
              </div>

              <Link to="/contact?mode=book" className="btn-primary mt-10 w-full" data-testid="pricing-book-cta">
                Book a free consultation
              </Link>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <p className="mt-12 max-w-2xl text-sm leading-relaxed text-[#A8B4C2]">
            Sempresera specializes in companies of up to 25 employees. We don't chase enterprise accounts — small
            business isn't our entry level, it's our entire focus. Small by choice.
          </p>
        </Reveal>
      </section>

      <VendorMarquee />
      <CTABanner />
    </>
  );
}
